import React, { useState } from "react";
import { CollatedUpdate } from "../../../types/collatedUpdates";
import { relativeTime, absoluteTime } from "./relativeTime";
import { sanitizeBody } from "./sanitizeBody";
import { Tooltip } from "@vibe/core";
import Avatar from "./Avatar";
// Read-only mode: posting replies is disabled for now. Kept for easy re-enable.
// import ReplyComposer from "./composer/ReplyComposer";

// Renders monday-authored rich-text HTML. Sanitizing lives here so both the
// top-level body and reply bodies always go through DOMPurify before render.
const Body = ({ html, className }: { html: string; className: string }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: sanitizeBody(html) }} />
);

// Toggle label, matching the mockup: the reply count (pluralized) when
// collapsed, "Hide replies" when open.
const replyToggleLabel = (count: number, open: boolean): string =>
  open ? "Hide replies" : `${count} ${count === 1 ? "reply" : "replies"}`;

const ActivityEntry = ({ entry }: { entry: CollatedUpdate }) => {
  const [repliesOpen, setRepliesOpen] = useState(false);
  // Read-only mode: the reply composer is disabled, so no open/close state is
  // needed. Kept commented for easy re-enable.
  // const [composerOpen, setComposerOpen] = useState(false);
  const hasReplies = entry.replies.length > 0;

  return (
    <div className="relative px-4 pt-4 pb-3.5 border-b border-t-0 border-x-0 border-solid border-[#F4F5F8]">
      <div className="flex gap-[11px] items-start">
        <Avatar
          initials={entry.author.initials}
          color={entry.author.color}
          photoUrl={entry.author.photoUrl}
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm font-semibold text-[#323338]">{entry.author.name}</span>
            {/* Tooltip shows the exact date/time (monday style) in the viewer's timezone. */}
            <Tooltip content={absoluteTime(entry.createdAt)}>
              <span className="text-xs text-[#9699A6]">{relativeTime(entry.createdAt)}</span>
            </Tooltip>
            {entry.boardName && (
              <>
                <span className="text-xs text-[#9699A6]">·</span>
                <span
                  title="Source board"
                  className="text-xs text-[#9699A6] whitespace-nowrap"
                >
                  {entry.boardName}
                </span>
              </>
            )}
          </div>
          <Body html={entry.body} className="mt-1.5 text-sm text-[#323338] leading-[1.55]" />
          <div className="mt-2.5 flex items-center gap-4">
            {hasReplies && (
              <button
                type="button"
                onClick={() => setRepliesOpen((v) => !v)}
                aria-label="Toggle replies"
                className="bg-transparent border-none cursor-pointer !font-figtree p-0 text-[12px] font-semibold text-[#0073EA]"
              >
                {replyToggleLabel(entry.replies.length, repliesOpen)}
              </button>
            )}
            {/* Read-only mode: the "Reply" action is disabled for now. Kept
                for easy re-enable of posting replies.
            <button
              type="button"
              aria-label="Reply"
              onClick={() => {
                setRepliesOpen(true);
                setComposerOpen(true);
              }}
              className="bg-transparent border-none cursor-pointer !font-figtree p-0 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#676879] hover:text-[#0073EA]"
            >
              <svg
                aria-hidden
                width="13"
                height="13"
                viewBox="0 0 20 20"
                fill="none"
                className="flex-none"
              >
                <path
                  d="M8 5L3 9.5 8 14M3.4 9.5H12a5 5 0 0 1 5 5V15"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Reply
            </button>
            */}
          </div>
          {/* Read-only mode: only existing replies are shown; the composer is
              gone, so the old `|| composerOpen` branch no longer applies.
              Old gate: {((repliesOpen && hasReplies) || composerOpen) && ( */}
          {repliesOpen && hasReplies && (
            <div className="mt-3 pl-3.5 border-l-2  border-y-0 border-r-0 border-solid border-[#F2F3F6] flex flex-col gap-4">
              {repliesOpen &&
                entry.replies.map((r) => (
                  <div key={r.id} className="flex gap-2.5 items-start">
                    <Avatar
                      initials={r.author.initials}
                      color={r.author.color}
                      photoUrl={r.author.photoUrl}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-[13px] font-semibold text-[#323338]">
                          {r.author.name}
                        </span>
                        <Tooltip content={absoluteTime(r.createdAt)}>
                          <span className="text-[11px] text-[#9699A6]">
                            {relativeTime(r.createdAt)}
                          </span>
                        </Tooltip>
                      </div>
                      <Body html={r.body} className="mt-1 text-[13px] text-[#323338] leading-[1.5]" />
                    </div>
                  </div>
                ))}
              {/* Read-only mode: the inline reply composer is disabled for now.
                  Kept for easy re-enable of posting replies.
              {composerOpen && (
                <div className="mt-1">
                  <ReplyComposer
                    parentUpdateId={entry.id}
                    onClose={() => setComposerOpen(false)}
                  />
                </div>
              )}
              */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityEntry;
